import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';

import PropTypes from 'prop-types';

import { ENVS, getEnv } from '@adsk/acs-sc-web-platform-api';

import { useFeatureFlag } from '../components/LaunchDarklyProvider';
import OverrideOverlay from '../components/OverrideOverlay/OverrideOverlay';
import usePreview from '../hooks/usePreview';
import { getRemoteOverrides } from '../utils/getRemoteOverrides';
import safeParseObjectFromSessionStorage from '../utils/safeParseObjectFromSessionStorage';

const RemoteOverridesContext = createContext<Overrides>({});
export const useRemoteOverrides = () => useContext(RemoteOverridesContext);

export const OVERRIDE_SESSION_STORAGE_KEY = 'ACS_EXO_REMOTE_OVERRIDES';

type Overrides = {
  [key: string]: string;
};

const RemoteOverridesProvider: FC<{
  children?: ReactNode;
  isEnvConfigLoaded: boolean;
}> = ({ children, isEnvConfigLoaded }) => {
  const { value: enableRemoteOverridesFF } = useFeatureFlag('remote-overrides');
  const { preview, onPreviewResetClick } = usePreview();
  const currentEnv = useMemo(() => getEnv(), []);

  const isRemoteOverridesEnabled = useMemo(
    () =>
      isEnvConfigLoaded && (currentEnv === ENVS.QA || enableRemoteOverridesFF),
    [isEnvConfigLoaded, currentEnv, enableRemoteOverridesFF],
  );

  const onRemoteOverrideResetClick = (scope: string) => {
    const overridesFromSessionStorage = safeParseObjectFromSessionStorage(
      OVERRIDE_SESSION_STORAGE_KEY,
    );

    delete overridesFromSessionStorage[scope];

    sessionStorage.setItem(
      OVERRIDE_SESSION_STORAGE_KEY,
      JSON.stringify(overridesFromSessionStorage),
    );
    window.history.pushState({}, '', window.location.pathname);
    window.location.reload();
  };

  const overrides = useMemo(
    () => (isRemoteOverridesEnabled ? getRemoteOverrides() : {}),
    [isRemoteOverridesEnabled],
  );

  const shouldDisplayOverrideOverlay = useMemo(() => {
    if (!isRemoteOverridesEnabled) {
      return false;
    }

    return preview || Object.values(overrides).length > 0;
  }, [isRemoteOverridesEnabled, preview, overrides]);

  return (
    <RemoteOverridesContext.Provider value={overrides}>
      {shouldDisplayOverrideOverlay && (
        <OverrideOverlay
          preview={preview}
          onPreviewResetClick={onPreviewResetClick}
          onRemoteOverrideResetClick={onRemoteOverrideResetClick}
        />
      )}
      {children}
    </RemoteOverridesContext.Provider>
  );
};

RemoteOverridesProvider.propTypes = {
  children: PropTypes.node,
  isEnvConfigLoaded: PropTypes.bool.isRequired,
};

export default RemoteOverridesProvider;
